import { Observable, exhaustMap, filter, interval, map } from 'rxjs';
import { DocumentsConstants } from '../constants/documents.constants';
import { DocumentStatus } from '../enums/data.enums';
import { DocumentService } from '../services/document.service';

/**
 * Centralized scan-status polling for Document Center files. A committed upload starts Pending and then
 * flips to Available (scan cleared) or Quarantined (Defender flagged it) server-side; this polls those
 * transitions so any UI showing the status updates without a manual reload.
 *
 * Reused by the Document Center grid and the email-composer attachment list: each caller supplies the
 * ids still Pending on its screen and applies the emitted resolutions to its own data structure.
 */
export class DocumentStatusUtilities {

    /**
     * Emits a map of documentId → resolved DocumentStatus (Available/Quarantined) whenever one or more
     * of the currently-Pending documents clear:
     *  - ticks every `intervalMs` (default 30s);
     *  - `getPendingIds` is read each tick — an empty list (or a backgrounded tab) is a cheap no-op;
     *  - cache-busted `getDocumentById` so it always sees the server's latest;
     *  - `exhaustMap` skips a tick while the previous poll's requests are still in flight (no overlap);
     *  - only still-Pending → resolved transitions are emitted (still-Pending ids are left alone).
     * The caller subscribes (with takeUntilDestroyed) and applies the map to its list; it owns teardown.
     */
    public static pollResolvedStatuses(
        documentService: DocumentService,
        getPendingIds: () => number[],
        intervalMs: number = DocumentsConstants.PENDING_STATUS_POLL_INTERVAL_MS()
    ): Observable<Map<number, DocumentStatus>> {
        return interval(intervalMs).pipe(
            // Skip ticks while the tab is backgrounded — the caller reconciles on return via its own load.
            map(() => (document.visibilityState === 'visible' ? getPendingIds() : [])),
            filter(ids => ids.length > 0),
            exhaustMap(ids => DocumentStatusUtilities.fetchResolvedStatuses(documentService, ids)),
            filter(resolved => resolved.size > 0)
        );
    }

    private static async fetchResolvedStatuses(
        documentService: DocumentService,
        ids: number[]
    ): Promise<Map<number, DocumentStatus>> {
        const results = await Promise.all(ids.map(id => documentService.getDocumentById(id, true)));
        const resolved = new Map<number, DocumentStatus>();
        for (const result of results) {
            if (result.success && result.data && result.data.status !== DocumentStatus.Pending) {
                resolved.set(result.data.documentId, result.data.status);
            }
        }
        return resolved;
    }
}
